/**
 * 轻量级事件总线：评论回复增删通知
 *
 * 当用户提交一条回复评论后，CommentsSection 通过此总线通知
 * 对应的 CommentItem 组件将新回复追加到其本地 loadedReplies state。
 *
 * 当用户删除子评论后，通知父 CommentItem 从 loadedReplies 中移除。
 */

import type { CommunityComment } from "./types";

// ── 新回复事件 ──

type ReplyListener = (reply: CommunityComment) => void;

const listeners = new Map<string, Set<ReplyListener>>();

/** CommentItem 调用：订阅某个 parentId 下的新回复 */
export function subscribeNewReply(
  parentId: string,
  listener: ReplyListener,
): () => void {
  if (!listeners.has(parentId)) {
    listeners.set(parentId, new Set());
  }
  listeners.get(parentId)!.add(listener);
  return () => {
    const set = listeners.get(parentId);
    if (set) {
      set.delete(listener);
      if (set.size === 0) listeners.delete(parentId);
    }
  };
}

/** CommentsSection 调用：发布新回复事件 */
export function emitNewReply(parentId: string, reply: CommunityComment): void {
  const set = listeners.get(parentId);
  if (set) {
    for (const fn of set) fn(reply);
  }
}

// ── 删除回复事件 ──

type DeleteListener = (commentId: string) => void;

const deleteListeners = new Map<string, Set<DeleteListener>>();

/** CommentItem 调用：订阅自己子评论的删除事件 */
export function subscribeDeleteReply(
  parentId: string,
  listener: DeleteListener,
): () => void {
  if (!deleteListeners.has(parentId)) {
    deleteListeners.set(parentId, new Set());
  }
  deleteListeners.get(parentId)!.add(listener);
  return () => {
    const set = deleteListeners.get(parentId);
    if (set) {
      set.delete(listener);
      if (set.size === 0) deleteListeners.delete(parentId);
    }
  };
}

/** 删除子评论后广播，通知所有可能的父级 */
export function emitDeleteReply(parentId: string, commentId: string): void {
  const set = deleteListeners.get(parentId);
  if (set) {
    for (const fn of set) fn(commentId);
  }
}
